/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "reactstrap";

type PaginationProps = {
    totalRecords: number;
    pageLimit: number;
    onPageChanged(page: number): void;
}

const Pagination = ({ totalRecords, pageLimit, onPageChanged }: PaginationProps) => {
    const [searchParams] = useSearchParams();
    const page = searchParams.get("page") ?? 1;
    const [currentPage, setCurrentPage] = useState(Number(page));

    const totalPages = useMemo(() => Math.ceil(totalRecords / pageLimit), [totalRecords, pageLimit]);

    useEffect(() => {
        if (currentPage !== page) {
            onPageChanged(currentPage);
        }
    }, [currentPage]);

    const showNavigationButtons = useMemo(() => totalPages > 7, [totalPages]);

    const paginationButtons = useMemo(() => Array.from({ length: totalPages > 7 ? 7 : totalPages }, (_, idx) => idx + 1), [totalPages]);

    const buttonValues = useMemo(() => {
        let middleButtons = [...paginationButtons.slice(1, 5), currentPage + 1];

        if (currentPage > 5 && currentPage + 4 < totalPages) {
            middleButtons = [
                // numbers are repeated because arrow will always go to next/prev page
                currentPage - 1,
                currentPage - 1,
                currentPage,
                currentPage + 1,
                currentPage + 1,
            ];
        }

        if (currentPage + 4 >= totalPages) {
            middleButtons = [
                currentPage - 1,
                totalPages - 4,
                totalPages - 3,
                totalPages - 2,
                totalPages - 1,
            ];
        }

        return [1, ...middleButtons, totalPages];
    }, [totalPages, currentPage]);

    const buttonLabels = useMemo(() => paginationButtons.map((item, idx) => {
        // First and last page are always the same
        if (!idx) return 1;
        if (idx === 6) return totalPages;

        // Arrow buttons always stay in same position
        if (idx === 1 && currentPage > 5) return <span key={`icon-${idx}`}>&laquo;</span>;
        if (idx === 5 && currentPage < totalPages - 4) return <span key={`icon-${idx}`}>&raquo;</span>;

        // if it's showing both arrow buttons, current page is always in the middle
        if (currentPage > 5 && currentPage < totalPages - 4) {
            if (idx === 2) return currentPage - 1;
            if (idx === 3) return currentPage;
            if (idx === 4) return currentPage + 1;
        }

        // showing only left arrow and last 5 pages
        if (currentPage > 5 && currentPage >= totalPages - 4) {
            if (idx === 2) return totalPages - 4;
            if (idx === 3) return totalPages - 3;
            if (idx === 4) return totalPages - 2;
            if (idx === 5) return totalPages - 1;
        }

        return item;
    }), [paginationButtons, currentPage, showNavigationButtons, totalPages]);

    return (
        <Fragment>
            <nav aria-label="Countries Pagination">
                <ul className="pagination pagination-sm mb-0 d-flex justify-content-start">
                    {(showNavigationButtons ? buttonValues : paginationButtons).map((item, idx) => (
                        <li key={`pagination-btn-${item}-${idx}`} className="page-item pe-2">
                            <Button
                                className="page-link"
                                aria-label={`Go to page ${item}`}
                                onClick={() => setCurrentPage(item)}
                                css={css`min-width: 35px;`}
                                active={currentPage === item}
                            >
                                {showNavigationButtons ? buttonLabels[idx] : item}
                            </Button>
                        </li>
                    ))}
                </ul>
            </nav>
        </Fragment>
    );
};

export default Pagination;
